import { useCallback } from 'react';
import { useFeature } from '@ole-ui/ole-ui-components';

import useTabs from 'hooks/useTabs';
import { EnablementFeatureFlags } from 'config/featureFlags';

import { CourseLandingTab } from '../types';

type UseCourseLandingTabsOptions = {
  courseSlug: string | undefined;
  route: { params?: Record<string, string> };
  routerStore: {
    navigate: (name: string, params: Record<string, string>) => void;
  };
  isInaccessible: boolean;
  hasPremiumSessionsInAnyVersion: boolean;
  /** Bundled (offline) packages do not offer live classes */
  isBundleCourse: boolean;
};

export default function useCourseLandingTabs({
  courseSlug,
  route,
  routerStore,
  isInaccessible,
  hasPremiumSessionsInAnyVersion,
  isBundleCourse,
}: UseCourseLandingTabsOptions) {
  const isWhatsNextEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.ShowWhatsNext,
    defaultFlagValue: false,
  });

  const isLiveSessionsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.CourseLiveSessionsTab,
    defaultFlagValue: false,
  });

  const isLiveClassEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.LiveClassEnabled,
    defaultFlagValue: false,
  });

  const showPremiumSessionsWithScheduleDisabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.ShowPremvtScheduleDisabled,
    defaultFlagValue: false,
  });

  const showLiveSessionsTab =
    !isInaccessible &&
    hasPremiumSessionsInAnyVersion &&
    (isLiveSessionsEnabled || showPremiumSessionsWithScheduleDisabled);

  const showLiveClassTab = isLiveClassEnabled && !isBundleCourse;
  const showWhatsNextTab = !isInaccessible && isWhatsNextEnabled;

  const isTabAvailable = useCallback(
    (tab: CourseLandingTab) => {
      switch (tab) {
        case CourseLandingTab.LiveClass:
          return showLiveClassTab;
        case CourseLandingTab.PremiumSessions:
          return showLiveSessionsTab;
        case CourseLandingTab.WhatsNext:
          return showWhatsNextTab;
        case CourseLandingTab.Overview:
        default:
          return true;
      }
    },
    [showLiveSessionsTab, showWhatsNextTab, showLiveClassTab],
  );

  const handleNavigate = useCallback(
    (tab: CourseLandingTab) => {
      const params: Record<string, string> = { course: courseSlug! };
      if (tab !== CourseLandingTab.Overview) params.tab = tab;
      routerStore.navigate('courses:overview', params);
    },
    [courseSlug, routerStore],
  );

  const { activeTabKey, handleTabClick } = useTabs<CourseLandingTab>({
    route,
    defaultTab: CourseLandingTab.Overview,
    isValidTab: (v): v is CourseLandingTab =>
      Object.values(CourseLandingTab).includes(v as CourseLandingTab),
    isTabAvailable,
    onNavigate: handleNavigate,
  });

  return {
    activeTabKey,
    handleTabClick,
    showLiveSessionsTab,
    showLiveClassTab,
    showWhatsNextTab,
  };
}
